import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { ChartRendererProps } from '../../types/chart-renderer-props';
import { toApexPieSeries } from '../../utils/to-apex-series';
import { ChartContainer } from '../shared/chart-container.component';
import { DonutCenterIcon } from './donut-center-icon.component';
import { getPieChartOptions } from './pie-chart-options';

export interface PieChartProps extends ChartRendererProps {
    variant?: 'pie' | 'donut';
    showSliceValues?: boolean;
    showLegend?: boolean;
    onSliceClick?: (label: string, index: number) => void;
}

export const PieChart = ({
    data,
    className,
    minHeight = 160,
    variant = 'donut',
    chartOptions,
    centerIcon,
    showSliceValues = false,
    showLegend = true,
    onSliceClick
}: PieChartProps) => {
    const { series, labels } = useMemo(() => toApexPieSeries(data), [data]);
    const containerClassName = `${className ?? 'relative h-full min-h-0 w-full overflow-visible'}${onSliceClick ? ' cursor-pointer' : ''}`;

    return (
        <ChartContainer
            className={containerClassName}
            minHeight={minHeight}>
            {(size) => {
                const baseOptions = getPieChartOptions({
                    labels,
                    height: size.height,
                    width: size.width,
                    type: variant,
                    animationsEnabled: !centerIcon,
                    showSliceValues,
                    showLegend,
                    expandOnClick: !onSliceClick
                });

                return (
                    <>
                        <ReactApexChart
                            options={{
                                ...baseOptions,
                                ...chartOptions,
                                ...(onSliceClick
                                    ? {
                                          chart: {
                                              ...baseOptions.chart,
                                              ...chartOptions?.chart,
                                              events: {
                                                  ...baseOptions.chart?.events,
                                                  ...chartOptions?.chart?.events,
                                                  dataPointSelection: (event, chartContext, config) => {
                                                      chartOptions?.chart?.events?.dataPointSelection?.(event, chartContext, config);
                                                      const index = config.dataPointIndex;
                                                      const label = labels[index];
                                                      if (index == null || index < 0 || !label) {
                                                          return;
                                                      }

                                                      onSliceClick(label, index);
                                                  }
                                              }
                                          }
                                      }
                                    : {})
                            }}
                            series={series}
                            type={variant}
                            height={size.height}
                            width={size.width}
                        />
                        {centerIcon && <DonutCenterIcon>{centerIcon}</DonutCenterIcon>}
                    </>
                );
            }}
        </ChartContainer>
    );
};
